/**
 * useEditMode - Hook para gestión de modo edición de coordenadas
 * V14 - Sistema de edición de coordenadas PTEL
 * 
 * Permite activar/desactivar modo edición y gestionar cambios pendientes
 * antes de confirmar o descartar.
 */

import { useState, useCallback, useMemo } from 'react';
import type { NormalizedInfrastructure } from '../../../types/infrastructure';

// =============================================================================
// TIPOS
// =============================================================================

/** Cambio de coordenadas pendiente */
export interface PendingEdit {
  /** ID de la infraestructura */
  infrastructureId: string;
  /** Nombre para mostrar */
  infrastructureName: string;
  /** Coordenadas originales */
  originalCoords: { x: number; y: number };
  /** Nuevas coordenadas propuestas */
  newCoords: { x: number; y: number };
  /** Timestamp del cambio */
  timestamp: Date;
  /** Fuente del cambio: 'drag' o 'click' */
  source: 'drag' | 'click';
}

/** Estado del modo edición */
export interface EditModeState {
  /** Si el modo edición está activo */
  isActive: boolean;
  /** Infraestructura actualmente seleccionada para editar */
  selectedId: string | null;
  /** Lista de cambios pendientes sin confirmar */
  pendingEdits: PendingEdit[];
  /** Si hay cambios sin guardar */
  hasUnsavedChanges: boolean;
  /** Si se ha alcanzado el límite de ediciones pendientes */
  isAtLimit: boolean;
  /** Slots restantes antes del límite */
  remainingSlots: number;
  /** Máximo de ediciones configurado */
  maxEdits: number;
}

/** Opciones del hook */
export interface UseEditModeOptions {
  /** Callback cuando se confirman los cambios */
  onConfirm?: (edits: PendingEdit[]) => void;
  /** Callback cuando se descartan los cambios */
  onDiscard?: () => void;
  /** Callback cuando cambia una coordenada */
  onCoordinateChange?: (edit: PendingEdit) => void;
  /** Callback cuando se alcanza o se acerca al límite de ediciones */
  onLimitReached?: (remaining: number, max: number) => void;
  /** Máximo de cambios pendientes antes de forzar confirmación */
  maxPendingEdits?: number;
}

/** Retorno del hook */
export interface UseEditModeReturn {
  /** Estado actual */
  state: EditModeState;
  /** Activar modo edición */
  enableEditMode: () => void;
  /** Desactivar modo edición (pregunta si hay cambios pendientes) */
  disableEditMode: () => boolean;
  /** Toggle modo edición */
  toggleEditMode: () => void;
  /** Seleccionar infraestructura para editar */
  selectForEdit: (id: string | null) => void;
  /** Registrar cambio de coordenadas (drag o click) */
  registerEdit: (
    infrastructure: NormalizedInfrastructure,
    newX: number,
    newY: number,
    source: 'drag' | 'click'
  ) => void;
  /** Deshacer último cambio */
  undoLastEdit: () => void;
  /** Deshacer cambio específico por ID */
  undoEdit: (infrastructureId: string) => void;
  /** Confirmar todos los cambios pendientes */
  confirmAllEdits: () => PendingEdit[];
  /** Descartar todos los cambios pendientes */
  discardAllEdits: () => void;
  /** Obtener coordenadas actuales (con cambios pendientes aplicados) */
  getCurrentCoords: (infrastructureId: string, original: { x: number; y: number }) => { x: number; y: number };
  /** Verificar si una infraestructura tiene cambios pendientes */
  hasEdit: (infrastructureId: string) => boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export function useEditMode(options: UseEditModeOptions = {}): UseEditModeReturn {
  const {
    onConfirm,
    onDiscard,
    onCoordinateChange,
    onLimitReached,
    maxPendingEdits = 50,
  } = options;

  // Estado
  const [isActive, setIsActive] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingEdits, setPendingEdits] = useState<PendingEdit[]>([]);

  // Estado derivado
  const state = useMemo<EditModeState>(() => ({
    isActive,
    selectedId,
    pendingEdits,
    hasUnsavedChanges: pendingEdits.length > 0,
    isAtLimit: pendingEdits.length >= maxPendingEdits,
    remainingSlots: Math.max(0, maxPendingEdits - pendingEdits.length),
    maxEdits: maxPendingEdits,
  }), [isActive, selectedId, pendingEdits, maxPendingEdits]);

  // Activar modo edición
  const enableEditMode = useCallback(() => {
    setIsActive(true);
  }, []);

  // Desactivar modo edición
  const disableEditMode = useCallback((): boolean => {
    if (pendingEdits.length > 0) {
      // Retorna false para indicar que hay cambios pendientes
      // El componente padre debe mostrar confirmación
      return false;
    }
    setIsActive(false);
    setSelectedId(null);
    return true;
  }, [pendingEdits.length]);

  // Toggle
  const toggleEditMode = useCallback(() => {
    if (isActive) {
      disableEditMode();
    } else {
      enableEditMode();
    }
  }, [isActive, enableEditMode, disableEditMode]);

  // Seleccionar para editar
  const selectForEdit = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  // Registrar cambio
  const registerEdit = useCallback((
    infrastructure: NormalizedInfrastructure,
    newX: number,
    newY: number,
    source: 'drag' | 'click'
  ) => {
    const edit: PendingEdit = {
      infrastructureId: infrastructure.id,
      infrastructureName: infrastructure.name,
      originalCoords: { x: infrastructure.x, y: infrastructure.y },
      newCoords: { x: newX, y: newY },
      timestamp: new Date(),
      source,
    };

    setPendingEdits(prev => {
      // Reemplazar edición existente del mismo ID o añadir nueva
      const filtered = prev.filter(e => e.infrastructureId !== infrastructure.id);
      const updated = [...filtered, edit];
      
      // Avisar al acercarse o alcanzar el límite
      const remaining = Math.max(0, maxPendingEdits - updated.length);
      if (remaining <= 5) {
        onLimitReached?.(remaining, maxPendingEdits);
      }
      if (updated.length > maxPendingEdits) {
        console.warn(`[useEditMode] Máximo de ${maxPendingEdits} ediciones alcanzado, descartando las más antiguas`);
      }
      
      return updated.slice(-maxPendingEdits);
    });

    onCoordinateChange?.(edit);
  }, [maxPendingEdits, onCoordinateChange, onLimitReached]);

  // Deshacer último
  const undoLastEdit = useCallback(() => {
    setPendingEdits(prev => prev.slice(0, -1));
  }, []);

  // Deshacer específico
  const undoEdit = useCallback((infrastructureId: string) => {
    setPendingEdits(prev => prev.filter(e => e.infrastructureId !== infrastructureId));
  }, []);

  // Confirmar todos
  const confirmAllEdits = useCallback((): PendingEdit[] => {
    const edits = [...pendingEdits];
    onConfirm?.(edits);
    setPendingEdits([]);
    setIsActive(false);
    setSelectedId(null);
    return edits;
  }, [pendingEdits, onConfirm]);

  // Descartar todos
  const discardAllEdits = useCallback(() => {
    onDiscard?.();
    setPendingEdits([]);
    setSelectedId(null);
  }, [onDiscard]);

  // Obtener coordenadas actuales (con ediciones aplicadas)
  const getCurrentCoords = useCallback((
    infrastructureId: string,
    original: { x: number; y: number }
  ): { x: number; y: number } => {
    const edit = pendingEdits.find(e => e.infrastructureId === infrastructureId);
    return edit ? edit.newCoords : original;
  }, [pendingEdits]);

  // Verificar si tiene edición
  const hasEdit = useCallback((infrastructureId: string): boolean => {
    return pendingEdits.some(e => e.infrastructureId === infrastructureId);
  }, [pendingEdits]);

  return {
    state,
    enableEditMode,
    disableEditMode,
    toggleEditMode,
    selectForEdit,
    registerEdit,
    undoLastEdit,
    undoEdit,
    confirmAllEdits,
    discardAllEdits,
    getCurrentCoords,
    hasEdit,
  };
}

export default useEditMode;
